import { createClass as _createClass, inherits as _inherits, classCallCheck as _classCallCheck, get as _get, getPrototypeOf as _getPrototypeOf, createSuper as _createSuper } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { minZero } from './util/math.js';

/* eslint max-classes-per-file: ["error", 4] */

var Page = /*#__PURE__*/function () {
  function Page(measurements, itemTracker, previousPage) {
    _classCallCheck(this, Page);

    this.measurements = measurements;
    this.itemTracker = itemTracker;
    this.previousPage = previousPage;
    /** page offset in pixels */

    this.offset = 0;
    /** index of its first item in the set of all items */

    this.firstItemIndex = 0;
    /** Is this the first page? Not likely */

    this.isFirst = false;
    /** The number of nav buttons present on this page. This must be overridden by subclasses. */

    this.buttonCount = 2;
    this.calculateOffset();
    this.calculateFirstItemIndex();
  }
  /** The all-important offset calculation! This determines how many pixels from the left edge of the first page this page needs to be. When we flip to this page, this is the amount we need to feed into translateX() to show it properly. */


  _createClass(Page, [{
    key: "calculateOffset",
    value: function calculateOffset() {
      if (this.previousPage === undefined) return; // TODO: this won't work if the filmstrip outer div's padding is greater than the button
      // width. Maybe unlikely, but possible.

      this.offset = this.previousPage.offset - this.previousPage.width + this.backButtonDodge;
    }
    /** The number of pixels to account for the presence the back button, needed to calculate the width available for items to occupy. */

  }, {
    key: "calculateFirstItemIndex",

    /** In the list of all items, returns the index of the first item on this page. FirstPage can just return zero, but MiddlePage needs to actually do the math. Precalculated in constructors and stored on the object for efficiency. */
    value: function calculateFirstItemIndex() {
      this.firstItemIndex = this.previousPage === undefined ? 0 : this.previousPage.firstItemIndex + this.previousPage.itemSlotsAvailable;
    }
    /** Is there even anything yet? Maybe not*/

  }, {
    key: "hasItem",

    /** Test if a provided item is present in this page. */
    value: function hasItem(item) {
      return this.items.includes(item);
    }
    /** Life isn't perfect, and neither are page dimensions. This tells you if the page and the filmstrip's available width aren't exactly the same. Which usually, they're not. */

  }, {
    key: "toJSON",
    value: function toJSON() {
      var offset = this.offset,
          firstItemIndex = this.firstItemIndex,
          lastItemIndex = this.lastItemIndex,
          width = this.width,
          availableWidth = this.availableWidth,
          isFirst = this.isFirst,
          isLast = this.isLast;
      return {
        offset: offset,
        firstItemIndex: firstItemIndex,
        lastItemIndex: lastItemIndex,
        width: width,
        availableWidth: availableWidth,
        isFirst: isFirst,
        isLast: isLast
      };
    }
  }, {
    key: "backButtonDodge",
    get: function get() {
      var _a;

      return ((_a = this.previousPage) === null || _a === void 0 ? void 0 : _a.isFirst) ? minZero(this.measurements.get('buttonDodge') - this.measurements.get('paddingLeft')) : 0;
    }
  }, {
    key: "filmstripIsEmpty",
    get: function get() {
      return this.itemTracker.filmstripIsEmpty;
    }
    /** Index (in the set of all items) of this page's final item */

  }, {
    key: "lastItemIndex",
    get: function get() {
      return Math.max(0, this.firstItemIndex + this.itemSlotsAvailable - 1);
    }
    /** Page width in pixels */

  }, {
    key: "width",
    get: function get() {
      return this.itemSlotsAvailable * this.slotWidth;
    }
    /** The number of items on this page. Becuase either value could be zero, we check for finite-ness
    before returning. */

  }, {
    key: "itemSlotsAvailable",
    get: function get() {
      var availableWidth = this.availableWidth,
          slotWidth = this.slotWidth;
      if (slotWidth > availableWidth) return 1;
      var count = Math.floor(availableWidth / slotWidth);
      return Number.isFinite(count) ? count : 0;
    }
    /** True if at least one item on this page has been rendered. */

  }, {
    key: "isAtLeastPartiallyRendered",
    get: function get() {
      var lastRenderedItemIndex = this.itemTracker.renderedItems - 1;
      return lastRenderedItemIndex >= 0 && lastRenderedItemIndex >= this.firstItemIndex;
    }
    /** Returns an array of all the items belonging to this page. */

  }, {
    key: "items",
    get: function get() {
      return this.itemTracker.all.slice(this.firstItemIndex, this.lastItemIndex + 1);
    }
    /** Whether or not this page has items that overlap with the previous page. False for everything except LastPage. Needed for a11y logic. */

  }, {
    key: "hasOverlappingItems",
    get: function get() {
      return false;
    }
    /** Returns the first item on this page. */

  }, {
    key: "firstItem",
    get: function get() {
      return this.items[0];
    }
    /** Returns the last item on this page. */

  }, {
    key: "lastItem",
    get: function get() {
      return this.items[this.items.length - 1];
    }
  }, {
    key: "pageWidthIsNotExact",
    get: function get() {
      return this.availableWidth > this.width;
    }
    /** Aliased here so the code that uses is reads more clearly. Returns true if this page's first item is peeking out from behind the right edge of the last one. Needed for a11y logic. */

  }, {
    key: "partiallyVisibleOnPreviousPage",
    get: function get() {
      return this.pageWidthIsNotExact;
    }
    /** Determines whether this is the last page. Adjust this with care. */

  }, {
    key: "isLast",
    get: function get() {
      return this.itemTracker.virtualItems <= 1 || this.availableWidth === 0 || this.lastItemIndex >= this.itemTracker.lastIndex;
    }
    /** Convenience alias, since this is used frequently here */

  }, {
    key: "slotWidth",
    get: function get() {
      return this.itemTracker.measurements.get('itemWidth');
    }
    /** Get the amount of available width in the page -- but never a negative number, even if the
    filmstrip width is zero. We take whichever is less: the total filmstrip width, minus space taken
    up by buttons, or the inner dimensions of the child container. This is to account for the
    possibility of padding on the filmstrip or children containers, and also variable button width. If
    buttons are huge, they could take up more space than the padding on theout div. */

  }, {
    key: "availableWidth",
    get: function get() {
      return minZero(Math.min(this.measurements.get('childContainerSpace'), this.measurements.get('width') - this.totalButtonDodge));
    }
    /** The total space taken up by buttons on this page. */

  }, {
    key: "totalButtonDodge",
    get: function get() {
      return this.buttonCount * this.measurements.get('buttonDodge');
    }
    /** Factory function that will hand you the right Page object, depending on its situation! */

  }], [{
    key: "create",
    value: function create(measurements, itemTracker, previousPage) {
      /** eslint-disable no-use-before-define */
      var page;

      if (!previousPage) {
        page = new FirstPage(measurements, itemTracker);
      } else {
        page = new MiddlePage(measurements, itemTracker, previousPage);
        if (page.isLast) page = new LastPage(measurements, itemTracker, previousPage);
      }

      return page;
    }
  }]);

  return Page;
}();
var MiddlePage = /*#__PURE__*/function (_Page) {
  _inherits(MiddlePage, _Page);

  var _super = _createSuper(MiddlePage);

  function MiddlePage() {
    _classCallCheck(this, MiddlePage);

    return _super.apply(this, arguments);
  }

  return MiddlePage;
}(Page);
var FirstPage = /*#__PURE__*/function (_Page2) {
  _inherits(FirstPage, _Page2);

  var _super2 = _createSuper(FirstPage);

  function FirstPage() {
    var _this;

    _classCallCheck(this, FirstPage);

    _this = _super2.apply(this, arguments);
    _this.buttonCount = 1;
    _this.isFirst = true;
    return _this;
  }
  /** Any self-respecting FirstPage needs to be able to know how how much width there is that isn't taken up by the endcap. */


  _createClass(FirstPage, [{
    key: "availableWidthForNormalItems",
    get: function get() {
      return this.availableWidth - this.startEndcapWidth;
    }
    /** Since there's an endcap slot, we need to know how many NON-endcap slots there are. */

  }, {
    key: "normalItemSlots",
    get: function get() {
      return Math.floor(this.availableWidthForNormalItems / this.slotWidth);
    }
    /** The first page needs to know how wide a potential endcap is, after all. */

  }, {
    key: "startEndcapWidth",
    get: function get() {
      return this.itemTracker.measurements.get('startEndcapWidth');
    }
    /** FirstPage needs to handle the possibility that we have no items at all. Otherwise, the math is the same as the base class. Also: we have to be careful about calculating the number of item slots when an endcap is involved.*/

  }, {
    key: "itemSlotsAvailable",
    get: function get() {
      if (this.filmstripIsEmpty) return 0;
      if (this.startEndcapWidth > this.availableWidth) return 1;
      var endcapItemSlots = 1;
      var normalItemSlots = this.normalItemSlots;
      return Number.isFinite(normalItemSlots) ? endcapItemSlots + normalItemSlots : 0;
    }
    /** On a FirstPage, the width calculation must account for an endcap, which is permitted to have a different width than other items. */

  }, {
    key: "width",
    get: function get() {
      return this.startEndcapWidth + this.normalItemSlots * this.slotWidth;
    }
    /** Of course it isn't! This is the first page! */

  }, {
    key: "partiallyVisibleOnPreviousPage",
    get: function get() {
      return false;
    }
  }]);

  return FirstPage;
}(Page);
var LastPage = /*#__PURE__*/function (_MiddlePage) {
  _inherits(LastPage, _MiddlePage);

  var _super3 = _createSuper(LastPage);

  function LastPage() {
    var _this2;

    _classCallCheck(this, LastPage);

    _this2 = _super3.apply(this, arguments);
    _this2.buttonCount = 1;
    return _this2;
  }
  /*
    EXAMPLES OF THIS MATH
    --------------------
       * Example 1 *
    slots: 3, items: 26
       Unaltered:
    second to last page: 22, 23, 24
    - firstItemIndex: 22
    - lastItemIndex: 24
    last page: 25
    - firstItemIndex: 25
    - lastItemIndex: 25
       Revised:
    second to last page: 22, 23, 24
    - firstItemIndex: 22
    - lastItemIndex: 24
    last page: 23, 24, 25
    - firstItemIndex: 23
    - lastItemIndex: 25
  */

  /** In the list of all items, returns the index of the first item on this page. LastPage wants to
  not show a big empty page if there's only one item on it, so there can be some overlap with the
  items of the previous page. */


  _createClass(LastPage, [{
    key: "calculateFirstItemIndex",
    value: function calculateFirstItemIndex() {
      this.firstItemIndex = this.lastItemIndex - this.itemSlotsAvailable + 1;
    }
    /** The number of slots/items that this page has in common with the last page. It's weird, I
    know. */

  }, {
    key: "calculateOffset",

    /** Special version of this to account for overlap with the previous page! */
    value: function calculateOffset() {
      _get(_getPrototypeOf(LastPage.prototype), "calculateOffset", this).call(this);

      this.offset += this.slotOverlap * this.slotWidth;
    }
  }, {
    key: "slotOverlap",
    get: function get() {
      return this.itemSlotsAvailable - (this.lastItemIndex - this.previousPage.lastItemIndex);
    }
    /** Whether or not this page has items that overlap with the previous page. False for everything
    except LastPage. Needed for a11y logic. */

  }, {
    key: "hasOverlappingItems",
    get: function get() {
      return this.slotOverlap > 0;
    }
    /** Index (in the set of all items) of this page's final item */

  }, {
    key: "lastItemIndex",
    get: function get() {
      return this.itemTracker.lastIndex;
    }
  }]);

  return LastPage;
}(MiddlePage);

export { FirstPage, LastPage, MiddlePage, Page };
